/**
 * Student records API client (issue #10).
 *
 * Typed wrappers over the shared `api` client (which already injects the Bearer
 * token + X-Tenant header). The registrar directory/detail is admin-only; the
 * `/me` pair is the student's own self-service profile. Only the admin detail
 * response carries `internal_note` — the directory and self serializers omit it.
 */
import type { ManagedIdentityState } from "@/lib/api/accounts";
import { api } from "@/lib/api/client";
import { commitOptions } from "@/lib/api/commitSeams";

/** A keyed write sends its commit key; a caller without one is a legacy write with no receipt. */
const keyed = (key?: string) => (key ? commitOptions(key) : undefined);

export interface StudentListItem {
  id: string;
  sid: string;
  legal_name: string;
  preferred_name: string | null;
  honorific: string | null;
  status: string;
  program_id: string | null;
  program_name: string | null;
  cohort_id: string | null;
  cohort_name: string | null;
  city: string | null;
  country: string | null;
  gpa_cached: number | null;
}

export interface StudentProfile {
  id: string;
  sid: string;
  legal_name: string;
  preferred_name: string | null;
  honorific: string | null;
  gender: string | null;
  program_id: string | null;
  program_name: string | null;
  cohort_id: string | null;
  cohort_name: string | null;
  status: string;
  email: string | null;
  phone: string | null;
  country: string | null;
  city: string | null;
  address: string | null;
  emergency_contact: string | null;
  citizenship_note: string | null;
  gpa_cached: number | null;
}

/**
 * The versions a guarded student save is checked against: the student
 * record's, and its person's (ADR 0034). The server checks each only when the
 * save writes that row, and refuses a stale one with 409 and the record as it
 * now stands.
 */
export interface StudentVersions {
  updated_at: string;
  person_updated_at: string;
}

/** The versions a save hands back, as read. Absent means no check. */
export interface ExpectedStudentVersions {
  expected_updated_at?: string;
  expected_person_updated_at?: string;
}

export interface InternalNote {
  note: string | null;
  updated_at: string | null;
  updated_by: string | null;
}

export interface Enrollment {
  id: string;
  /** Absent only while an older backend is serving during a paired release. */
  course_id?: string;
  /** The course's term; null when the course has none. Absent as `course_id` is. */
  semester_name?: string | null;
  status: string;
  participation_kind: "credit" | "audit";
  code: string;
  title: string;
  meeting_days: string | null;
  meeting_time: string | null;
}

export interface StudentDetail {
  profile: StudentProfile & StudentVersions;
  internal_note: InternalNote | null;
  enrollments: Enrollment[];
}

export type LearnerAccessReadiness =
  | "student_inactive"
  | "not_rostered"
  | "access_pending"
  | "course_unavailable"
  | "ready";

export interface PostAdmissionReadiness {
  student_record: {
    created: true;
    id: string;
    sid: string;
    status: string;
  };
  admission: {
    state: "recorded" | "not_available";
    applicant_id: string | null;
    stage: string | null;
  };
  cohort: {
    state: "unassigned" | "active" | "archived";
    id: string | null;
    label: string | null;
  };
  current_invitation: {
    id: string;
    state: "provisioning" | "issued" | "claiming" | "accepted" | "revoked" | "superseded";
    provisioning_state: "legacy" | "pending" | "ready" | "failed";
    delivery_state: "pending" | "sent" | "failed" | "not_configured";
    expires_at: string;
    is_expired: boolean;
  } | null;
  account: {
    activated: boolean;
    active_membership: boolean;
    membership_role: string | null;
  };
  enrollment: {
    active_count: number;
    learner_visible_course_count: number;
  };
  learner_access: { state: LearnerAccessReadiness };
}

/**
 * How a student's `YYNNN` identifier was issued (#97).
 *
 * Null for every student whose identifier predates the standard sequence or was
 * seeded outside it (`MA-26-0142`, `AUD-26-031`): those are preserved as issued
 * and were never minted, so there is no issuance record to show.
 */
export interface SidIssuance {
  sid: string;
  cohort_year: number;
  cohort_year_source:
    | "applicant_round"
    | "applicant_applied_date"
    | "current_year";
  actor_user_id: string;
  issued_at: string;
}

/** A course's result: only a certification is a grade (ADR 0002). */
export type AcademicResultState = "withdrawn" | "certified" | "awaiting_certification" | "in_progress";

/** Why a certified credit course does not count toward GPA. */
export type AcademicResultNote = "no_credits" | "letter_not_on_scale";

/**
 * A two-place figure (GPA, points, score). The server sends numeric values as
 * text ("3.40"); read them through `academicFigure` before showing them.
 */
export type AcademicFigure = string | number;

export interface AcademicRecordCourse {
  enrollment_id: string;
  course_id: string;
  code: string;
  title: string;
  credits: number | null;
  participation_kind: "credit" | "audit";
  enrollment_status: string;
  /** Primary first. */
  instructors: { name: string; kind: "primary" | "co_instructor" }[];
  schedule: {
    weekdays: number[] | null;
    starts_at: string | null;
    ends_at: string | null;
    timezone: string | null;
    location: string | null;
    modality: string | null;
  };
  result: {
    state: AcademicResultState;
    letter: string | null;
    score: AcademicFigure | null;
    points: AcademicFigure | null;
    note: AcademicResultNote | null;
  };
}

export interface AcademicRecordTerm {
  /** Null for courses placed in no term. */
  semester_id: string | null;
  name: string | null;
  start_date: string | null;
  end_date: string | null;
  status: "active" | "upcoming" | "closed" | null;
  gpa: AcademicFigure | null;
  /** The certified credits the term GPA counts. */
  credits: number;
  /** A credit course in the term is still without a counted result. */
  provisional: boolean;
  courses: AcademicRecordCourse[];
}

export interface AcademicRecordYear {
  /** The year the academic year opens ("2026"), or "undated". */
  key: string;
  label: string;
  terms: AcademicRecordTerm[];
}

/** `GET /students/{id}/academic-record`: the registrar's derived academic record. */
export interface AcademicRecord {
  student: {
    id: string;
    sid: string;
    name: string | null;
    program: string | null;
    cohort: string | null;
    status: string;
  };
  cumulative: {
    gpa: AcademicFigure | null;
    certified_credits: number;
    provisional: boolean;
    flags: AcademicResultNote[];
  };
  standing: {
    state: "none" | "good" | "below";
    label: string;
    floor: AcademicFigure;
  };
  current_year_key: string;
  /** The current year and every year the student attended, newest first; undated last. */
  years: AcademicRecordYear[];
}

/** A two-place figure as text, "3.40", whichever form the server sent. */
export function academicFigure(value: AcademicFigure): string {
  return Number(value).toFixed(2);
}

export interface StudentFilters {
  q?: string;
  program_id?: string;
  status?: string;
  cohort_id?: string;
}

/** Registrar edit: any student field; `note` lands in the admin-only note table. */
export interface AdminStudentUpdate {
  legal_name?: string | null;
  preferred_name?: string | null;
  honorific?: string | null;
  gender?: string | null;
  program_id?: string | null;
  cohort_id?: string | null;
  email?: string | null;
  phone?: string | null;
  country?: string | null;
  city?: string | null;
  address?: string | null;
  emergency_contact?: string | null;
  citizenship_note?: string | null;
  note?: string | null;
}

export interface StudentLifecycleResult {
  student: StudentDetail;
  account: {
    user_id: string;
    role: string;
    is_active: boolean;
    is_super_admin: boolean;
    /** The same object `PATCH /accounts/{id}` returns - both routes converge
     *  through `converge_account_lifecycle_change`. Reusing the type rather
     *  than restating it keeps this from silently understating the response,
     *  which it already had begun to do. */
    managed_state: ManagedIdentityState | null;
  } | null;
}

export type ConversionRestoreStage =
  | "Applied"
  | "Under Review"
  | "Interview Invited"
  | "Decision Pending"
  | "Waitlisted"
  | "Denied";

export type StudentStanding = "active" | "leave_of_absence" | "completed";

export function getStudents(filters: StudentFilters = {}): Promise<StudentListItem[]> {
  const params = new URLSearchParams();
  if (filters.q) params.set("q", filters.q);
  if (filters.program_id) params.set("program_id", filters.program_id);
  if (filters.status) params.set("status", filters.status);
  if (filters.cohort_id) params.set("cohort_id", filters.cohort_id);
  const qs = params.toString();
  return api.get<StudentListItem[]>(`/students${qs ? `?${qs}` : ""}`);
}

export function getStudent(id: string): Promise<StudentDetail> {
  return api.get<StudentDetail>(`/students/${id}`);
}

export function getPostAdmissionReadiness(id: string): Promise<PostAdmissionReadiness> {
  return api.get<PostAdmissionReadiness>(`/students/${id}/post-admission-readiness`);
}

export function patchStudent(
  id: string,
  body: AdminStudentUpdate & ExpectedStudentVersions,
  key?: string,
): Promise<StudentDetail> {
  return api.patch<StudentDetail>(`/students/${id}`, body, keyed(key));
}

const versioned = (expectedUpdatedAt?: string) => (expectedUpdatedAt ? { expected_updated_at: expectedUpdatedAt } : {});

/** `expectedUpdatedAt` is the record's version as read (R4): a record changed since is refused, with it as it stands. */
export function withdrawStudent(id: string, reason: string, key?: string, expectedUpdatedAt?: string): Promise<StudentLifecycleResult> {
  return api.post<StudentLifecycleResult>(`/students/${id}/withdraw`, { reason, ...versioned(expectedUpdatedAt) }, keyed(key));
}

export function correctStudentConversion(
  id: string,
  reason: string,
  restoreStage: ConversionRestoreStage,
  key?: string,
  expectedUpdatedAt?: string,
): Promise<StudentLifecycleResult> {
  return api.post<StudentLifecycleResult>(`/students/${id}/correct-conversion`, {
    reason,
    restore_stage: restoreStage,
    ...versioned(expectedUpdatedAt),
  }, keyed(key));
}

export function restoreStudent(id: string, reason: string, key?: string, expectedUpdatedAt?: string): Promise<StudentLifecycleResult> {
  return api.post<StudentLifecycleResult>(`/students/${id}/restore`, { reason, ...versioned(expectedUpdatedAt) }, keyed(key));
}

/** A standing change on the record as read (R4): `expectedUpdatedAt` refuses a record changed since. */
export function setStudentStanding(
  id: string,
  status: StudentStanding,
  reason: string,
  key?: string,
  expectedUpdatedAt?: string,
): Promise<StudentDetail> {
  return api.post<StudentDetail>(
    `/students/${id}/standing`,
    { status, reason, ...(expectedUpdatedAt ? { expected_updated_at: expectedUpdatedAt } : {}) },
    keyed(key),
  );
}

export function getStudentSidIssuance(id: string): Promise<SidIssuance | null> {
  return api.get<SidIssuance | null>(`/students/${id}/issuance`);
}

export function getStudentAcademicRecord(id: string): Promise<AcademicRecord> {
  return api.get<AcademicRecord>(`/students/${id}/academic-record`);
}

/** The one read the student record's facts and its academic record register share. */
export function academicRecordQuery(id: string) {
  return {
    queryKey: ["student", id, "academic-record"],
    queryFn: () => getStudentAcademicRecord(id),
  };
}
